import { call, put, takeEvery } from "redux-saga/effects";

import * as apis from "../../../network/apis/companyPortalDashboard";
import { showErrorMsg } from "../../../utils/Helpers";
import * as actions from "./actions";
import * as types from "./types";

function* getCompanyDashboardOverviewSaga() {
  try {
    const response = yield call(apis.getCompanyPortalDashboardOverview);
    yield put(
      actions.getCompanyDashboardOverviewResponse(response.data?.data),
    );
  } catch (error) {
    yield put(actions.getCompanyDashboardOverviewFailure());
    // A network failure has no `error.response` — the axios interceptor
    // already shows "No internet connection" for it (see errorHandler);
    // showing it again here with an empty message left a blank toast.
    const message = error.response?.data?.message;
    if (message) {
      yield call(showErrorMsg, message);
    }
  }
}

function* getCompanyDashboardOngoingEventsSaga() {
  try {
    const response = yield call(apis.getCompanyPortalEventsOverview, {
      status: "ongoing",
      page: -1,
    });
    const data = response.data?.data;
    yield put(
      actions.getCompanyDashboardOngoingEventsResponse(
        Array.isArray(data) ? data : [],
      ),
    );
  } catch (error) {
    yield put(actions.getCompanyDashboardOngoingEventsFailure());
    const message = error.response?.data?.message;
    if (message) {
      yield call(showErrorMsg, message);
    }
  }
}

function* getCompanyDashboardClientSatisfactionSaga() {
  try {
    const response = yield call(apis.getCompanyPortalClientSatisfaction, {
      order_by_overall_client_satisfaction: "desc",
      page: -1,
    });
    const data = response.data?.data;
    yield put(
      actions.getCompanyDashboardClientSatisfactionResponse(
        Array.isArray(data) ? data : [],
      ),
    );
  } catch (error) {
    yield put(actions.getCompanyDashboardClientSatisfactionFailure());
    const message = error.response?.data?.message;
    if (message) {
      yield call(showErrorMsg, message);
    }
  }
}

const TOP_EVENTS_WINDOW_DAYS = 30;

function* getCompanyDashboardTopEventsSaga() {
  try {
    // The 30-day window is the client's to send (rolling, computed per call).
    // Filtered by close date (PROLINE-3877), not launch date: an event that
    // launched over 30 days ago but is still running (or just closed) belongs
    // in the table, and launch-date filtering was dropping it.
    const windowStart = new Date(
      Date.now() - TOP_EVENTS_WINDOW_DAYS * 24 * 60 * 60 * 1000,
    ).toLocaleDateString("en-CA");
    const response = yield call(apis.getCompanyPortalEventsOverview, {
      close_at_start_date: windowStart,
      order_by_number_of_approved_applicants: "desc",
      order_by_created_at: "desc",
      items: 3,
    });
    const data = response.data?.data;
    yield put(
      actions.getCompanyDashboardTopEventsResponse(
        Array.isArray(data) ? data : [],
      ),
    );
  } catch (error) {
    yield put(actions.getCompanyDashboardTopEventsFailure());
    const message = error.response?.data?.message;
    if (message) {
      yield call(showErrorMsg, message);
    }
  }
}

function* getCompanyDashboardRecentActivitySaga() {
  try {
    // First page only (PROLINE-3925): the section shows the 10 newest entries.
    const response = yield call(apis.getCompanyPortalActivityLogs, {
      page: 1,
      items: 10,
    });
    const data = response.data?.data;
    yield put(
      actions.getCompanyDashboardRecentActivityResponse(
        Array.isArray(data) ? data : [],
      ),
    );
  } catch (error) {
    yield put(actions.getCompanyDashboardRecentActivityFailure());
    const message = error.response?.data?.message;
    if (message) {
      yield call(showErrorMsg, message);
    }
  }
}

export default function* companyDashboardSagas() {
  yield takeEvery(
    types.GET_COMPANY_DASHBOARD_OVERVIEW_REQUEST,
    getCompanyDashboardOverviewSaga,
  );
  yield takeEvery(
    types.GET_COMPANY_DASHBOARD_ONGOING_EVENTS_REQUEST,
    getCompanyDashboardOngoingEventsSaga,
  );
  yield takeEvery(
    types.GET_COMPANY_DASHBOARD_CLIENT_SATISFACTION_REQUEST,
    getCompanyDashboardClientSatisfactionSaga,
  );
  yield takeEvery(
    types.GET_COMPANY_DASHBOARD_TOP_EVENTS_REQUEST,
    getCompanyDashboardTopEventsSaga,
  );
  yield takeEvery(
    types.GET_COMPANY_DASHBOARD_RECENT_ACTIVITY_REQUEST,
    getCompanyDashboardRecentActivitySaga,
  );
}
